// M4 E2 — Create workspace modal.
//
// Minimal dialog: single "name" input + Create/Cancel. Plan is hard-coded to
// "free" until M5 wires Stripe. On success, we set the new workspace as
// active, which triggers the switcher and every workspace-aware component
// (evidence list, etc.) to refetch via subscribeWorkspaceChanges.

import { useEffect, useRef, useState } from "react";
import { createWorkspace, setCurrentWorkspaceId } from "../lib/workspace";

interface Props {
  open: boolean;
  onClose: () => void;
  // Fired after a successful create. The parent can refetch the list. The
  // active-workspace switch has already been done here.
  onCreated?: () => void;
}

export function CreateWorkspaceDialog({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Autofocus + Escape to close.
  useEffect(() => {
    if (!open) return;
    // Reset state when the modal opens fresh.
    setName("");
    setError(null);
    setSubmitting(false);
    // Focus after the modal renders.
    setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const trimmed = name.trim();
  const canSubmit = trimmed.length >= 2 && trimmed.length <= 60 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const ws = await createWorkspace(trimmed, "free");
      setCurrentWorkspaceId(ws.workspaceId);
      onCreated?.();
      onClose();
      // Full reload so any workspace-unaware code paths (unlikely at this
      // point, but cheap insurance) pick up the new context cleanly.
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create workspace");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-ws-title"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm border border-border">
        <div className="px-5 py-4 border-b border-border">
          <h2 id="create-ws-title" className="text-base font-semibold text-ink">Create workspace</h2>
          <p className="text-xs text-ink-soft mt-1">
            A workspace groups evidence and members. You can invite people after creation.
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="text-xs text-ink-soft">Workspace name</span>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              maxLength={60}
              placeholder="e.g. Acme Legal"
              disabled={submitting}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-aigentico-navy/30 disabled:bg-neutral-50"
            />
            <span className="text-xs text-ink-faint mt-1 block">
              2 to 60 characters.
            </span>
          </label>

          {error && (
            <div className="text-xs text-error-fg bg-error-bg border border-error-border rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-white hover:bg-neutral-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1.5 text-sm rounded-md bg-aigentico-navy text-white hover:bg-aigentico-navy/90 disabled:opacity-60"
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
